import React, {useEffect, useRef, useState} from "react";
import {useChatStore} from "../store/chatbotStore";
import "./chatbotUsers.css";
import {useSelector} from "react-redux";
import usersSelector from "../../users";
import {TitlePage} from "../../shared";
import {Helmet} from "react-helmet";
import icon from "../../../images/pageIcon.png";
import CustomModalV2 from "../../shared/custom-modal-v2/custom-modal-v2";
import {getCachedImageUrl} from "../../../utils/utils";
import {SquadDiskUsersApi} from "../../../apis/squaddisk-users/SquadDiskUsersApi";

// Lista de permisos disponibles

const PERMISSIONS = [
    "admin", "createUser", "viewUsers", "deleteUsers", "editUserPassword", "editUserRoles", "viewTobiAI", "towersstreamDashboard", "viewSquadflixLogs"
];

// Hook personalizado para lazy loading
const useLazyImage = (src) => {
    const [imageSrc, setImageSrc] = useState(null);
    const [imageRef, setImageRef] = useState();

    useEffect(() => {
        let observer;

        if (imageRef && src) {
            observer = new IntersectionObserver(
                entries => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            setImageSrc(src);
                            observer.unobserve(imageRef);
                        }
                    });
                },
                {threshold: 0.1}
            );
            observer.observe(imageRef);
        }

        return () => {
            if (observer && imageRef) {
                observer.unobserve(imageRef);
            }
        };
    }, [imageRef, src]);

    return [setImageRef, imageSrc];
};

// Componente LazyImage
const LazyImage = ({src, alt, style, getUserInitials, user}) => {
    const [setImageRef, imageSrc] = useLazyImage(getCachedImageUrl(src));

    return (
        <div ref={setImageRef} style={style}>
            {imageSrc ? (
                <img
                    style={{
                        height: "100%",
                        width: "100%",
                        borderRadius: "50%",
                        objectFit: "cover"
                    }}
                    src={imageSrc}
                    alt={alt}
                />
            ) : (
                getUserInitials(user)
            )}
        </div>
    );
};

export default function ChatbotUsers() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [modalUser, setModalUser] = useState(null); // usuario para modal de roles
    const [modalPerms, setModalPerms] = useState({});
    const [savingPerms, setSavingPerms] = useState(false);
    const hasPermission = useChatStore((s) => s.hasPermission);
    const darkMode = useSelector(usersSelector.selectors.isDarkMode);
    const [selectedUserDetails, setSelectedUserDetails] = useState(null);

    useEffect(() => {
        setLoading(true);
        SquadDiskUsersApi.getAllUsers()
            .then(res => {
                if (res.data) {
                    setUsers(res.data.map(u => ({
                        ...u,
                        permissions: u.permissions || {admin: u.isAdmin}
                    })).sort((a, b) => {
                        return new Date(b.lastLogin || 0) - new Date(a.lastLogin || 0);
                    }));
                } else {
                    setError(res.error || "Error al cargar usuarios");
                }
                setLoading(false);
            })
            .catch(e => {
                setError(e.message || "Error al cargar usuarios");
                setLoading(false);
            });
    }, []);

    // Buscador
    const filteredUsers = users.filter(u =>
        (u.email?.toLowerCase().includes(search.toLowerCase()) ||
            u.displayName?.toLowerCase().includes(search.toLowerCase()))
    );


    // Modal roles usando CustomModal
    const closeButtonRef = useRef(null);
    const openRolesModal = async (user) => {
        setModalUser(user);
        setModalPerms({});
        try {
            const res = await SquadDiskUsersApi.getUserRights(user.uid);
            if (res && res.data && res.data.permissions) {
                setModalPerms(res.data.permissions);
            } else {
                setModalPerms({});
            }
        } catch (e) {
            setModalPerms({});
        }
    };
    const closeRolesModal = () => {
        // Cierra usando el botón oculto de CustomModal
        if (closeButtonRef.current) closeButtonRef.current.click();
        setTimeout(() => {
            setModalUser(null);
            setModalPerms({});
            setSavingPerms(false);
        }, 200);
    };
    const handlePermChange = (perm) => {
        setModalPerms(p => ({...p, [perm]: !p[perm]}));
    };
    const savePerms = async () => {
        if (!modalUser) return;
        setSavingPerms(true);
        try {
            await SquadDiskUsersApi.updateUserPermissions(modalUser.uid, Object.keys(modalPerms).filter(k => modalPerms[k]));
            setUsers(users.map(u => u.uid === modalUser.uid ? {...u, permissions: {...modalPerms}} : u));
            closeRolesModal();
        } catch (e) {
            alert("Error actualizando permisos: " + (e.message || e));
            setSavingPerms(false);
        }
    };

    const handleDelete = async (uid) => {
        if (!window.confirm("¿Eliminar usuario?")) return;
        try {
            await SquadDiskUsersApi.deleteUser(uid);
            setUsers(users.filter(u => u.uid !== uid));
        } catch (e) {
            alert("Error eliminando usuario: " + (e.message || e));
        }
    };

    // Función para obtener las iniciales del usuario
    const getUserInitials = (user) => {
        if (user.displayName) {
            return user.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
        }
        return user.email ? user.email[0].toUpperCase() : 'U';
    };

    if (loading) return <div className="chatbot-users__loading">Cargando usuarios...</div>;
    if (error) return <div className="chatbot-users__error">Error: {error}</div>;

    return (
        <>
            <Helmet>
                <meta charSet="utf-8"/>
                <title>SquadDisk | Usuarios</title>
                <link rel="icon" href={icon}/>
            </Helmet>

            <TitlePage title="Usuarios de SquadDisk" icon="fa-solid fa-users" leftClass="col-12"
                       disableScroll={true}/>

            <div className={`chatbot-users ${darkMode ? 'chatbot-users-dark' : ''}`}>
                <input
                    type="text"
                    className="chatbot-users__search"
                    placeholder="Buscar por nombre o email..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />

                <div className="chatbot-users__grid">
                    {filteredUsers.map(u => (
                        <div key={u.uid} className="chatbot-users__card">
                            <div className="chatbot-users__card-header">
                                <div className="chatbot-users__card-avatar">
                                    {u.photoURL ? (
                                        <LazyImage
                                            src={u.photoURL}
                                            alt={u.displayName || u.email}
                                            style={{
                                                height: "100%",
                                                width: "100%",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center"
                                            }}
                                            getUserInitials={getUserInitials}
                                            user={u}
                                        />
                                    ) : (
                                        getUserInitials(u)
                                    )}
                                </div>
                                {u.isAdmin && (
                                    <div className="chatbot-users__card-admin-badge">
                                        Admin
                                    </div>
                                )}
                            </div>

                            <div className="chatbot-users__card-info">
                                <div className="chatbot-users__card-name">
                                    {u.displayName || 'Sin nombre'}
                                </div>
                                <div className="chatbot-users__card-email">
                                    {u.email}
                                </div>
                            </div>

                            <div className="chatbot-users__card-meta">
                                Último login: {u.lastLogin ? new Date(u.lastLogin).toLocaleString() : "Nunca"}
                            </div>

                            <div className="chatbot-users__card-actions">
                                {hasPermission("editUserRoles") && (
                                    <button
                                        className="chatbot-users__card-button chatbot-users__card-button--secondary"
                                        onClick={() => openRolesModal(u)}
                                    >
                                        Roles
                                    </button>
                                )}
                                <button
                                    className="chatbot-users__card-button chatbot-users__card-button--secondary"
                                    onClick={() => setSelectedUserDetails(u)}
                                >
                                    Detalles
                                </button>
                                {hasPermission("deleteUsers") && (
                                    <button
                                        className="chatbot-users__card-button chatbot-users__card-button--danger"
                                        onClick={() => handleDelete(u.uid)}
                                    >
                                        Eliminar
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <CustomModalV2
                    title="Detalles del usuario"
                    open={selectedUserDetails !== null}
                    onClose={() => setSelectedUserDetails(null)}>
                    {selectedUserDetails && (
                        <iframe title="User Profile Details" style={{width: '100%', height: '80vh', border: 'none'}}
                                src={`/user/${selectedUserDetails.displayName}/${selectedUserDetails.customClaims?.userCode}`}/>
                    )}
                </CustomModalV2>

                {modalUser && (
                    <div className="chatbot-users__modal" onClick={closeRolesModal}>
                        <div
                            className="chatbot-users__modal-content"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                ref={closeButtonRef}
                                className="chatbot-users__modal-close"
                                onClick={closeRolesModal}
                            >
                                ×
                            </button>

                            <h3 className="chatbot-users__modal-title">
                                Permisos de {modalUser.displayName || modalUser.email}
                            </h3>

                            <div className="chatbot-users__permissions">
                                {PERMISSIONS.map(perm => (
                                    <div key={perm} className="chatbot-users__permission">
                                        <input
                                            type="checkbox"
                                            id={`perm-${perm}`}
                                            checked={!!modalPerms[perm]}
                                            disabled={!hasPermission("editUserRoles")}
                                            onChange={() => handlePermChange(perm)}
                                        />
                                        <label htmlFor={`perm-${perm}`}>
                                            {perm}
                                        </label>
                                    </div>
                                ))}
                            </div>

                            {hasPermission("editUserRoles") && (
                                <div className="chatbot-users__modal-actions">
                                    <button
                                        className="chatbot-users__card-button chatbot-users__card-button--secondary"
                                        onClick={closeRolesModal}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        className="chatbot-users__card-button"
                                        onClick={savePerms}
                                        disabled={savingPerms}
                                    >
                                        {savingPerms ? "Guardando..." : "Guardar cambios"}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
